import { useEffect, useState, useCallback } from "react";
import { Unlock, FileText, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/common";
import { OpenCashDialog } from "@/components/cash/OpenCashDialog";
import { CashSessionCard } from "@/components/cash/CashSessionCard";
import { CashReport } from "@/components/cash/CashReport";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDateTime, canDo, isManager } from "@/lib/api";
import { toast } from "sonner";

function History({ onOpen }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/cash/sessions").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="card-surface overflow-x-auto">
      <p className="eyebrow border-b p-4">Histórico de caixas</p>
      <Table data-testid="cash-history-table">
        <TableHeader><TableRow><TableHead>Caixa</TableHead><TableHead>Abertura</TableHead><TableHead>Fechamento</TableHead><TableHead className="text-right">Vendas</TableHead><TableHead className="text-right">Diferença</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {list.map((s) => (
            <TableRow key={s.id} data-testid={`cash-history-row-${s.id}`}>
              <TableCell className="font-mono">#{s.number || "—"} {s.status === "open" && <span className="ml-1 text-xs text-emerald-500">aberto</span>}<br /><span className="font-sans text-xs text-muted-foreground">{s.kind === "ticket" ? "Ticket" : "PDV"}{s.user_names?.length ? ` · ${s.user_names.join(", ")}` : ""}</span></TableCell>
              <TableCell className="text-xs">{fmtDateTime(s.opened_at)}<br />{s.opened_by}</TableCell>
              <TableCell className="text-xs">{s.closed_at ? <>{fmtDateTime(s.closed_at)}<br />{s.closed_by}</> : "—"}</TableCell>
              <TableCell className="text-right font-mono">{s.summary ? brl(s.summary.sales_total) : "—"}</TableCell>
              <TableCell className={`text-right font-mono ${(s.total_difference ?? s.difference) < 0 ? "text-red-500" : ""}`}>{s.status === "closed" ? brl(s.total_difference ?? s.difference) : "—"}</TableCell>
              <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => onOpen(s.id)} data-testid={`cash-history-report-${s.id}`}><FileText className="h-4 w-4 mr-1" />Relatório</Button></TableCell>
            </TableRow>
          ))}
          {!list.length && <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">Nenhum caixa registrado.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CashPage() {
  const { session } = useAuth();
  const u = session.user;
  const manager = isManager(u);
  const [items, setItems] = useState([]);
  const [opening, setOpening] = useState(null);
  const [report, setReport] = useState(null);
  const [histKey, setHistKey] = useState(0);
  const load = useCallback(() => api.get("/cash/open-sessions").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); setHistKey((k) => k + 1); };
  const openReport = async (id) => { try { setReport((await api.get(`/cash/sessions/${id}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  const pdvOpen = items.some((i) => i.session.kind !== "ticket");
  return (
    <>
      <PageHeader eyebrow="Controle de caixa" title="Caixa"
        subtitle={manager ? "Abra, acompanhe e feche os caixas dos usuários" : "Somente o administrador ou gerente abre e fecha o caixa"}
        actions={manager && <>
          <Button onClick={() => setOpening("pdv")} disabled={pdvOpen} data-testid="cash-open-pdv-btn"><Unlock className="h-4 w-4 mr-1" />Abrir caixa PDV</Button>
          <Button variant="outline" onClick={() => setOpening("ticket")} data-testid="cash-open-ticket-btn"><Unlock className="h-4 w-4 mr-1" />Abrir caixa ticket</Button>
        </>} />
      <div className="space-y-4" data-testid="cash-open-sessions">
        {items.map((it) => <CashSessionCard key={it.session.id} item={it} manager={manager} onChanged={reload} />)}
        {!items.length && <EmptyState icon={Wallet} title="Nenhum caixa aberto" text={manager ? "Abra o caixa PDV e selecione os usuários, ou abra o caixa individual da Venda ticket." : "Aguarde o administrador ou gerente abrir o seu caixa."} />}
      </div>
      {(canDo(u, "view_reports") || canDo(u, "close_cash")) && <History key={histKey} onOpen={openReport} />}
      {opening && <OpenCashDialog mode={opening} defaultUserId={u.id} onClose={() => setOpening(null)} onDone={() => { setOpening(null); reload(); }} />}
      <CashReport s={report} store={session.tenant.nome_fantasia} onClose={() => setReport(null)} />
    </>
  );
}
